/*
 * Copyright (c) 2022, WSO2 LLC (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */
import { useIntl } from 'react-intl';
import React, { useEffect, useState }  from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Badge, Box, Tooltip, Typography } from '@material-ui/core';
import {
  ApiReducer,
  ApplicationState,
  OrganizationMetadataReducer,
  SwaggerReducer,
  UserReducer
} from '../../../data/interfaces';
import { getAPIType, isThirdPartyApi, renderSkeleton } from '../../../data/utils';
import { useStyles, verbChipColors } from './style';
import OutlineButton from '../../../elements/OutlineButton';
import CustomIcons from '../../../icons/CustomIcons';
import { HTTP_METHODS, PATH_SEPARATOR, WS_METHODS } from '../../../data/constants';
import { fetchSwagger, fetchSwaggerUnauthenticated } from '../../../data/operations';

interface Operation {
  verb: string;
  path: string;
  'x-wso2-new': string;
  description: string;
}

interface ApiResourcesProps {
  organizationHandle: string,
  selectedEnvironment?: string,
  apiState: ApiReducer,
  swagger: SwaggerReducer;
  user: UserReducer,
  organizationMetadataReducer: OrganizationMetadataReducer,
  dispatchFetchSwagger: (apiId: string, organizationId: string, environmentName?: string, apiType?: string) => void,
  dispatchFetchSwaggerUnauthenticated: (apiId: string, organizationId: string, environmentName?: string, apiType?: string) => void,
  displayCount?: number;
}

const ApiResources = (props: ApiResourcesProps) => {
  const intl = useIntl();
  const history = useHistory();
  const classes = useStyles();
  const {
    organizationHandle,
    selectedEnvironment,
    swagger,
    swagger: {
      swagger: { paths },
      loading
    },
    apiState: {
      api,
      loading: apiLoading
    },
    user: {
      selectedOrg: { handle: selectedOrgHandleName, uuid: selectedOrgUuid },
      apimTokenObtained,
    },
    organizationMetadataReducer: {
      organizationMetadata: { uuid: organizationMetadataUuid },
    },
    dispatchFetchSwagger,
    dispatchFetchSwaggerUnauthenticated,
    displayCount = 5
  } = props;
  const [operations, setOperations] = useState<Operation[]>([]);

  // Load the definition of the revision deployed to the selected gateway environment, since the
  // resources can differ between environments. Third-party (advertised) APIs are not deployed to
  // a gateway environment, so their definition is not environment specific.
  useEffect(() => {
    if (!selectedEnvironment || !api?.id || isThirdPartyApi(api)) {
      return;
    }
    if (apimTokenObtained) {
      dispatchFetchSwagger(api.id, selectedOrgUuid, selectedEnvironment, getAPIType(api));
    } else {
      dispatchFetchSwaggerUnauthenticated(api.id, organizationMetadataUuid, selectedEnvironment, getAPIType(api));
    }
  }, [selectedEnvironment, api?.id]);

  useEffect(() => {
    if (paths) {
      const operationList = Object.entries(paths).map(([path, verbs]) => (
        Object.entries(verbs)
            .filter(([verb, operation]: [string, any]) => Object.keys(HTTP_METHODS).some(method => method.toLowerCase() === verb.toLowerCase()))
          .map(([verb, operation]: [string, any]) => ({
            verb,
            path,
            'x-wso2-new': operation['x-wso2-new'],
            description: operation.description
          }))
      ));
      setOperations(operationList.flat());
    }
  }, [paths]);

  useEffect(() => {
    if (getAPIType(api) === 'WS') {
      const { channels } = swagger.swagger;
      const topicList: Operation[] = [];
      for (const topic in channels) {
        const channel = channels[topic];
        for (const verb in channel) {
          if (
            verb.slice(0, 3) === WS_METHODS.PUBLISH ||
            verb.slice(0, 3) === WS_METHODS.SUBSCRIBE
          ) {
            topicList.push({
              verb: verb.slice(0, 3),
              path: topic,
              "x-wso2-new": "false",
              description: "",
            });
          }
      }
    }
      setOperations(topicList.flat());
  }
  }, [swagger, api]);

  const handleViewMoreClick = () => {
    if (apimTokenObtained) {
      history.push(`${PATH_SEPARATOR}${selectedOrgHandleName}/apis/${api?.id}/tryout`);
    } else {
      history.push(`${PATH_SEPARATOR}${organizationHandle}/apis/${api?.id}/resources`);
    }
  };
  
  if (loading) {
    return (renderSkeleton('rect', '100%', 48));
  }

  return (
    <Box aria-label='api-resources'>
      {operations.slice(0, displayCount).map((operation) => (
        <Box
          className={classes.operation}
          key={`${operation.verb}/${operation.path}`}
          display='flex'
          alignItems='center'
          width={1}
          borderRadius={4}
          p={1}
          mb={1}
        >
          <Box>
            <Badge
              invisible={!operation['x-wso2-new']}
              color="error"
              variant="dot"
            >
              <Box
                width={60}
                height={24}
                borderRadius={4}
                display="flex"
                justifyContent="center"
                alignItems="center"
                bgcolor={verbChipColors[operation.verb]}
                className={classes.verb}
              >
                {operation.verb.toUpperCase()}
              </Box>
            </Badge>
          </Box>
          <Box
            pr={2}
            pl={2}
            overflow="hidden"
          >
            <Tooltip
              title={operation.path}
              interactive
              aria-label="Resource Path"
            >
              <Typography variant="body1" noWrap>
                {operation.path}
              </Typography>
            </Tooltip>
          </Box>
          {operation.description && (
            <Box pr={1} maxWidth="40%">
              <Tooltip
                title={operation.description}
                interactive
                aria-label="Resource Summary"
              >
                <Typography variant="body2" noWrap>
                  {operation.description}
                </Typography>
              </Tooltip>
            </Box>
          )}
        </Box>
      ))}
      {operations.length > displayCount && (
        <Box
          display='flex'
          justifyContent='flex-start'
          pt={1}
        >
          <OutlineButton
            data-testid="button-try-out"
            onClick={handleViewMoreClick}
            buttonLabel={intl.formatMessage({
              id: "components.api.ApiResources.ApiResources.view.more",
              defaultMessage: "View more"
            })}
            startIcon={<CustomIcons icon="try-button-icon" />}
          />
        </Box>
      )}
    </Box>
  );
};

const mapStateToProps = (state: ApplicationState) => {
  return {
    apiState: state.apiReducer,
    swagger: state.swaggerReducer,
    user: state.userReducer,
    organizationMetadataReducer: state.organizationMetadataReducer
  };
};

const mapDispatchToProps = {
  dispatchFetchSwagger: fetchSwagger,
  dispatchFetchSwaggerUnauthenticated: fetchSwaggerUnauthenticated
};

export default connect(mapStateToProps, mapDispatchToProps)(ApiResources);
